import Head from "next/head";
import { TbShirtSport } from "react-icons/tb";
import { HiOutlineTicket } from "react-icons/hi";
import { MdOutlineEventSeat, MdOutlinedFlag } from "react-icons/md";
import { Box, Link, List, ListItem, Stack, Typography } from "@mui/material";

import { createState } from "@/global/store/zustand_store";
import { Divider } from "@/shared/base/components/Divider/Divider";
import { NotificationModal } from "@/shared/common/InfoPopup/NotificationModal";

import constants from "@/global/config/constants";

import type { TeamConfig } from "@/global/hooks/useTeamConfig";

export const howItWorks = [
  {
    icon: <MdOutlinedFlag />,
    title: "Choose your games.",
    description: "Select the games you wish to attend.",
  },
  {
    icon: <MdOutlineEventSeat />,
    title: "Login to MLB.com",
    description: (
      <>
        Click Continue and connect your Cincinnati Reds account using our secure{" "}
        <Link target="_blank" href="https://www.MLB.com/">
          MLB.com
        </Link>{" "}
        login.
      </>
    ),
  },
  {
    icon: <TbShirtSport />,
    title: "Select Your Seats",
    description: "Select the seats you wish to purchase for each game.",
  },

  {
    icon: <HiOutlineTicket />,
    title: "Get Your Tickets",
    description:
      "Tickets will be available in your Cincinnati Reds account. Reach out to Live Fan Support for any questions.",
  },
];

export const faqs = [
  {
    title: "When will I receive my tickets?",
    description:
      "If your reservation is accepted, a Cincinnati Reds representative will be in contact with further information, including ticket delivery details.",
  },
  {
    title: "Is re-entry allowed?",
    description:
      "Re-entry is allowed only at specific gate locations. Before exiting the stadium, please confirm which gates allow re-entry with a Cincinnati Reds guest services representative.",
  },
  {
    title: "What is included in my suite?",
    description:
      "If your reservation is accepted, a Cincinnati Reds representative will be in contact with further information, including details regarding parking and food and beverage options.",
  },
];

export const CheckoutTerms = () => (
  <>
    In connection with your reservation, you will receive ticket-related
    commercial emails from{" "}
    <Link
      target="_blank"
      sx={{
        fontWeight: 500,
        color: "primary.main",
        textDecoration: "none",
      }}
      href="http://reds.com/"
    >
      reds.com
    </Link>
    . You may unsubscribe at any time. By checking this box you agree to receive
    additional commercial emails from{" "}
    <Link
      target="_blank"
      sx={{
        fontWeight: 500,
        color: "primary.main",
        textDecoration: "none",
      }}
      href="http://reds.com/"
    >
      reds.com
    </Link>{" "}
    and{" "}
    <Link
      target="_blank"
      href="http://MLB.com"
      sx={{
        fontWeight: 500,
        color: "primary.main",
        textDecoration: "none",
      }}
    >
      MLB.com | The Official Site of Major League Baseball
    </Link>
    . The terms and conditions of your ticket and policies applicable to its use
    are available online at{" "}
    <Link
      target="_blank"
      sx={{
        fontWeight: 500,
        color: "primary.main",
        textDecoration: "none",
      }}
      href="https://www.mlb.com/reds/tickets/terms-and-conditions"
    >
      Reds Ticket Terms and Conditions | Cincinnati Reds
    </Link>
    . By clicking the Purchase Tickets button, you agree to accept all Terms and
    Conditions. Your purchase is not complete until a Cincinnati Reds
    representative connects with you to complete your purchase. Upon completing
    your purchase, you acknowledge and agree that your credit card will be
    charged. By clicking &apos;Purchase Tickets&apos;, you agree to the MLB
    Privacy Policy, Terms of Use and to receive your ticket(s) via an SMS text
    message to the mobile # associated with your Cincinnati Reds account.
    Message and data rates may apply. Tickets will be made available in the MLB
    Ballpark mobile application only.{" "}
    <Link
      target="_blank"
      href="http://MLB.com"
      sx={{
        fontWeight: 500,
        color: "primary.main",
        textDecoration: "none",
      }}
    >
      MLB.com | The Official Site of Major League Baseball
    </Link>{" "}
    and supported mobile device required.
  </>
);

export const AdditionalDetailsDisclaimerTC = () => (
  <>
    By entering my name, checking the box above and clicking Submit Request, I
    hereby (i) agree to the{" "}
    <Link
      target="_blank"
      href="https://www.mlb.com/reds/tickets/terms-and-conditions"
    >
      Terms of Use
    </Link>{" "}
    (ii) agree that my reservation will not be complete unless and until I
    receive a notice from the Cincinnati Reds confirming whether or not my
    reservation has been accepted in accordance with our agreement.
  </>
);

export const triplePlayPartySuiteNames = [
  "Triple Play Party Suite #13",
  "Triple Play Party Suite #24",
  "Triple Play Party Suite #8",
];

export const homePlateSuperSuiteNames = ["Home Plate Super Suite"];
export const thirdBaseSuperSuiteNames = ["Third Base Super Suite"];
export const outFieldSuperSuiteNames = ["Outfield Super Suite"];
export const executiveSuperSuiteNames = ["Executive Super Suite"];
export const centerFieldPavilionSuiteNames = ["Centerfield Pavilion"];
export const machineRoomPatioSuiteNames = ["Machine Room Patio"];
export const partyDeckSuiteNames = ["Party Deck A", "Party Deck B"];
export const powerAlleyPatioSuiteNames = ["Power Alley Patio"];
export const skybarSuiteNames = ["Skybar"];
export const fujitecSuperSuiteNames = ["Fujitec Super Suite"];
export const allInclusiveSuiteNames = [
  "Luxury Suite 101",
  "Luxury Suite 102",
  "Luxury Suite 103",
  "Luxury Suite 104",
  "Luxury Suite 105",
  "Luxury Suite 106",
  "Luxury Suite 107",
  "Luxury Suite 108",
  "Luxury Suite 109",
  "Luxury Suite 110",
  "Luxury Suite 111",
  "Luxury Suite 112",
  "Luxury Suite 113",
  "Luxury Suite 114",
  "Luxury Suite 115",
  "Luxury Suite 116",
  "Luxury Suite 203",
  "Luxury Suite 204",
  "Luxury Suite 205",
  "Luxury Suite 206",
  "Luxury Suite 207",
  "Luxury Suite 208",
  "Luxury Suite 209",
  "Luxury Suite 211",
  "Luxury Suite 212",
  "Luxury Suite 213",
  "Luxury Suite 214",
  "Luxury Suite 215",
  "Luxury Suite 216",
  "Luxury Suite 218",
  "Luxury Suite 219",
  "Luxury Suite 220",
  "Luxury Suite 221",
  "Luxury Suite 222",
  "Luxury Suite 223",
  "Luxury Suite 224",
  "Luxury Suite 225",
  "Luxury Suite 226",
  "Luxury Suite 227",
  "Luxury Suite 228",
  "Luxury Suite 229",
];

export const inventoryCardSuiteModal = createState({
  title: "",
  isOpen: false,
  content: <></>,
});

export const InventoryCard = () => {
  const modal = inventoryCardSuiteModal();
  return (
    <NotificationModal
      hideTitleIcon
      hidePrimaryButton
      title={modal.title}
      hideSecondaryButton
      isOpen={modal.isOpen}
      renderFooter={() => null}
      onClose={() => modal.set({ isOpen: false })}
      paperProps={{ style: { marginLeft: 2, marginRight: 2 } }}
      renderBody={() => <Box p={1}>{modal.content}</Box>}
    />
  );
};

export const TriplePlayPartySuiteContent = () => (
  <Box p={2} pt={1}>
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Typography
        variant="h6"
        sx={{
          fontWeight: 600,
          minWidth: "135px",
          fontSize: "1.25rem",
          color: "primary.main",
        }}
      >
        Triple Play Suite
      </Typography>
      <Divider sx={{ flex: 1, marginLeft: 2 }} orientation="horizontal" />
    </Stack>

    <List sx={{ py: 0 }}>
      <ListItem sx={{ paddingX: 0 }}>
        <Typography variant="body2">
          • <b>Capacity</b>: 25-32 guests per suite{" "}
          <i>
            (3 adjacent suites: #13, #8, and #24 - 2 can be combined for 50-64
            guests or all 3 can be combined for 75-99 guests)
          </i>
          <br />
          <br />• <b>Food & Beverage</b>: All-inclusive standard buffet & two
          beers per guest -{" "}
          <i>
            opportunity to upgrade package to upscale buffet & full beverage
            service for $31/guest
          </i>
          <br />
          <br />• <b>Experience</b>: Private, spacious, climate-controlled suite
          down left-field line with private restroom and personal suite
          attendant
          <br />
          <br />• <b>Seating</b>: Combination of indoor lounge seating and
          outdoor patio & ballpark seating with luxury furnishings and HD TVs
          <br />
          <br />
          For more info{" "}
          <Link
            target="_blank"
            sx={{
              fontWeight: 500,
              color: "primary.main",
              textDecoration: "none",
            }}
            href="https://www.mlb.com/reds/tickets/group/triple-play"
          >
            Click Here
          </Link>
          <br />
        </Typography>
      </ListItem>
    </List>
  </Box>
);

export const HomePlateSuperSuiteContent = () => (
  <Box p={2} pt={1}>
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Typography
        variant="h6"
        sx={{
          fontWeight: 600,
          minWidth: "135px",
          fontSize: "1.25rem",
          color: "primary.main",
        }}
      >
        Home Plate Super Suite
      </Typography>
      <Divider sx={{ flex: 1, marginLeft: 2 }} orientation="horizontal" />
    </Stack>

    <List sx={{ py: 0 }}>
      <ListItem sx={{ paddingX: 0 }}>
        <Typography variant="body2">
          • <b>Capacity</b>: Accommodates 30-40 guests
          <br />
          <br />• <b>Food & Beverage</b>: Upscale, all-inclusive buffet with
          full bar service, soft drinks, and water
          <br />
          <br />• <b>Experience</b>: Private, spacious, climate-controlled suite
          with premium views behind home plate, a dedicated restroom and
          personal suite attendant
          <br />
          <br />• <b>Seating</b>: Combination of indoor lounge seating and
          outdoor ballpark seating with luxury furnishings and HD TVs
          <br />
          <br />• <b>Parking</b>: Includes 2 complimentary parking passes
          <br />
          <br />
          For more info{" "}
          <Link
            target="_blank"
            sx={{
              fontWeight: 500,
              color: "primary.main",
              textDecoration: "none",
            }}
            href="https://www.mlb.com/reds/tickets/group/home-plate"
          >
            Click Here
          </Link>
          <br />
        </Typography>
      </ListItem>
    </List>
  </Box>
);

export const ThirdBaseSuperSuiteContent = () => (
  <Box p={2} pt={1}>
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Typography
        variant="h6"
        sx={{
          fontWeight: 600,
          minWidth: "135px",
          fontSize: "1.25rem",
          color: "primary.main",
        }}
      >
        Third Base Super Suite
      </Typography>
      <Divider sx={{ flex: 1, marginLeft: 2 }} orientation="horizontal" />
    </Stack>

    <List sx={{ py: 0 }}>
      <ListItem sx={{ paddingX: 0 }}>
        <Typography variant="body2">
          • <b>Capacity</b>: Accommodates 30-40 guests
          <br />
          <br />• <b>Food & Beverage</b>: Upscale, all-inclusive buffet with
          full bar service, soft drinks, and water
          <br />
          <br />• <b>Experience</b>: Private, spacious, climate-controlled suite
          with premium views behind third base, a dedicated restroom and
          personal suite attendant
          <br />
          <br />• <b>Seating</b>: Combination of indoor lounge seating and
          outdoor ballpark seating with luxury furnishings and HD TVs
          <br />
          <br />• <b>Parking</b>: Includes 2 complimentary parking passes
          <br />
          <br />
          For more info{" "}
          <Link
            target="_blank"
            sx={{
              fontWeight: 500,
              color: "primary.main",
              textDecoration: "none",
            }}
            href="https://www.mlb.com/reds/tickets/group/cincinnati-casino"
          >
            Click Here
          </Link>
          <br />
        </Typography>
      </ListItem>
    </List>
  </Box>
);

export const OutFieldSuperSuiteContent = () => (
  <Box p={2} pt={1}>
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Typography
        variant="h6"
        sx={{
          fontWeight: 600,
          minWidth: "135px",
          fontSize: "1.25rem",
          color: "primary.main",
        }}
      >
        Outfield Super Suite
      </Typography>
      <Divider sx={{ flex: 1, marginLeft: 2 }} orientation="horizontal" />
    </Stack>

    <List sx={{ py: 0 }}>
      <ListItem sx={{ paddingX: 0 }}>
        <Typography variant="body2">
          • <b>Capacity</b>: Accommodates 25-45 guests
          <br />
          <br />• <b>Food & Beverage</b>: All-inclusive standard buffet & two
          beers per guest -{" "}
          <i>
            opportunity to upgrade package to upscale buffet & full beverage
            service for $31/guest
          </i>
          <br />
          <br />• <b>Experience</b>: Private, spacious, climate-controlled suite
          down left-field line with private restroom and personal suite
          attendant
          <br />
          <br />• <b>Seating</b>: Combination of indoor lounge seating and
          outdoor ballpark seating with luxury furnishings and HD TVs
          <br />
          <br />• <b>Parking</b>: Includes 2 complimentary parking passes
          <br />
          <br />
          For more info{" "}
          <Link
            target="_blank"
            sx={{
              fontWeight: 500,
              color: "primary.main",
              textDecoration: "none",
            }}
            href="https://www.mlb.com/reds/tickets/group/outfield"
          >
            Click Here
          </Link>
          <br />
        </Typography>
      </ListItem>
    </List>
  </Box>
);

export const ExecutiveSuiteContent = () => (
  <Box p={2} pt={1}>
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Typography
        variant="h6"
        sx={{
          fontWeight: 600,
          minWidth: "135px",
          fontSize: "1.25rem",
          color: "primary.main",
        }}
      >
        Executive Super Suite
      </Typography>
      <Divider sx={{ flex: 1, marginLeft: 2 }} orientation="horizontal" />
    </Stack>

    <List sx={{ py: 0 }}>
      <ListItem sx={{ paddingX: 0 }}>
        <Typography variant="body2">
          • <b>Capacity</b>: Accommodates 35-50 guests
          <br />
          <br />• <b>Food & Beverage</b>: All-inclusive standard buffet & two
          beers per guest -{" "}
          <i>
            opportunity to upgrade package to upscale buffet & full beverage
            service for $31/guest
          </i>
          <br />
          <br />• <b>Experience</b>: Private, spacious, climate-controlled
          suite. Open-air concept with sliding glass wall. Dedicated restroom
          and personal suite attendant
          <br />
          <br />• <b>Seating</b>: Combination of indoor lounge seating and
          outdoor ballpark seating with luxury furnishings and HD TVs
          <br />
          <br />• <b>Parking</b>: Includes 2 complimentary parking passes
          <br />
          <br />
          For more info{" "}
          <Link
            target="_blank"
            sx={{
              fontWeight: 500,
              color: "primary.main",
              textDecoration: "none",
            }}
            href="https://www.mlb.com/reds/tickets/group/executive-suite"
          >
            Click Here
          </Link>
          <br />
        </Typography>
      </ListItem>
    </List>
  </Box>
);

export const CenterFieldPavilionSuiteContent = () => (
  <Box p={2} pt={1}>
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Typography
        variant="h6"
        sx={{
          fontWeight: 600,
          minWidth: "135px",
          fontSize: "1.25rem",
          color: "primary.main",
        }}
      >
        Centerfield Pavilion
      </Typography>
      <Divider sx={{ flex: 1, marginLeft: 2 }} orientation="horizontal" />
    </Stack>

    <List sx={{ py: 0 }}>
      <ListItem sx={{ paddingX: 0 }}>
        <Typography variant="body2">
          • <b>Capacity</b>: Accommodates 60-115 guests
          <br />
          <br />• <b>Food & Beverage</b>: All-inclusive standard buffet & two
          beers per guest -{" "}
          <i>
            opportunity to upgrade package to upscale buffet & full beverage
            service for $31/guest
          </i>
          <br />
          <br />• <b>Experience</b>: Private, spacious, indoor/outdoor party
          area located in center field batter's eye with a dedicated restroom
          and personal suite attendant
          <br />
          <br />• <b>Seating</b>: Climate-controlled indoor hospitality area
          with drink rails & lounge seating. Access to Bullpen Decks located
          above the Reds' bullpen featuring low half-moon tables, drink rails &
          bar stools.
          <br />
          <br />
          For more info{" "}
          <Link
            target="_blank"
            sx={{
              fontWeight: 500,
              color: "primary.main",
              textDecoration: "none",
            }}
            href="https://www.mlb.com/reds/tickets/group/pavillion"
          >
            Click Here
          </Link>
          <br />
        </Typography>
      </ListItem>
    </List>
  </Box>
);

export const MachineRoomPatioSuiteContent = () => (
  <Box p={2} pt={1}>
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Typography
        variant="h6"
        sx={{
          fontWeight: 600,
          minWidth: "135px",
          fontSize: "1.25rem",
          color: "primary.main",
        }}
      >
        Machine Room Patio
      </Typography>
      <Divider sx={{ flex: 1, marginLeft: 2 }} orientation="horizontal" />
    </Stack>

    <List sx={{ py: 0 }}>
      <ListItem sx={{ paddingX: 0 }}>
        <Typography variant="body2">
          • <b>Capacity</b>: Accommodates 20-48 guests
          <br />
          <br />• <b>Food & Beverage</b>: All-inclusive standard buffet & two
          beers per guest -{" "}
          <i>
            opportunity to upgrade package to upscale buffet & full beverage
            service for $31/guest
          </i>
          <br />
          <br />• <b>Experience</b>: Private, spacious, outdoor party area down
          left-field line with a personal attendant
          <br />
          <br />• <b>Seating</b>: Partially covered outdoor patio & seating
          <br />
          <br />
          For more info{" "}
          <Link
            target="_blank"
            sx={{
              fontWeight: 500,
              color: "primary.main",
              textDecoration: "none",
            }}
            href="https://www.mlb.com/reds/tickets/group/machine-room"
          >
            Click Here
          </Link>
          <br />
        </Typography>
      </ListItem>
    </List>
  </Box>
);

export const PartyDeckSuiteContent = () => (
  <Box p={2} pt={1}>
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Typography
        variant="h6"
        sx={{
          fontWeight: 600,
          minWidth: "135px",
          fontSize: "1.25rem",
          color: "primary.main",
        }}
      >
        Party Decks
      </Typography>
      <Divider sx={{ flex: 1, marginLeft: 2 }} orientation="horizontal" />
    </Stack>

    <List sx={{ py: 0 }}>
      <ListItem sx={{ paddingX: 0 }}>
        <Typography variant="body2">
          • <b>Capacity</b>: Accommodates 50-70 guests per deck{" "}
          <i>(2 adjacent decks - can be combined for 100- 140 guests)</i>
          <br />
          <br />• <b>Food & Beverage</b>: All-inclusive standard buffet & two
          beers per guest -{" "}
          <i>
            opportunity to upgrade package to upscale buffet & full beverage
            service for $31/guest
          </i>
          <br />
          <br />• <b>Experience</b>: Private, spacious, outdoor party area down
          right-field line with a dedicated restroom and personal attendant
          <br />
          <br />• <b>Seating</b>: Semi-covered decks, drink rails and cushioned
          ballpark seating
          <br />
          <br />
          For more info{" "}
          <Link
            target="_blank"
            sx={{
              fontWeight: 500,
              color: "primary.main",
              textDecoration: "none",
            }}
            href="https://www.mlb.com/reds/tickets/group/party-decks"
          >
            Click Here
          </Link>
          <br />
        </Typography>
      </ListItem>
    </List>
  </Box>
);

export const PowerAlleyPatioSuiteContent = () => (
  <Box p={2} pt={1}>
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Typography
        variant="h6"
        sx={{
          fontWeight: 600,
          minWidth: "135px",
          fontSize: "1.25rem",
          color: "primary.main",
        }}
      >
        Power Alley Patio
      </Typography>
      <Divider sx={{ flex: 1, marginLeft: 2 }} orientation="horizontal" />
    </Stack>

    <List sx={{ py: 0 }}>
      <ListItem sx={{ paddingX: 0 }}>
        <Typography variant="body2">
          • <b>Capacity</b>: Accommodates 30-50 guests
          <br />
          <br />• <b>Food & Beverage</b>: All-inclusive standard buffet & two
          beers per guest -{" "}
          <i>
            opportunity to upgrade package to upscale buffet & full beverage
            service for $31/guest
          </i>
          <br />
          <br />• <b>Experience</b>: Patio-style party area located in
          center-field with personal attendant
          <br />
          <br />• <b>Seating</b>: Partially covered outdoor patio & seating with
          two drink rails
          <br />
          <br />
          For more info{" "}
          <Link
            target="_blank"
            sx={{
              fontWeight: 500,
              color: "primary.main",
              textDecoration: "none",
            }}
            href="https://www.mlb.com/reds/tickets/group/patio"
          >
            Click Here
          </Link>
          <br />
        </Typography>
      </ListItem>
    </List>
  </Box>
);

export const SkybarSuiteContent = () => (
  <Box p={2} pt={1}>
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Typography
        variant="h6"
        sx={{
          fontWeight: 600,
          minWidth: "135px",
          fontSize: "1.25rem",
          color: "primary.main",
        }}
      >
        Skybar
      </Typography>
      <Divider sx={{ flex: 1, marginLeft: 2 }} orientation="horizontal" />
    </Stack>

    <List sx={{ py: 0 }}>
      <ListItem sx={{ paddingX: 0 }}>
        <Typography variant="body2">
          • <b>Capacity</b>: Accommodates 20-40 guests
          <br />
          <br />• <b>Food & Beverage</b>: All-inclusive standard buffet & two
          beers per guest -{" "}
          <i>
            opportunity to upgrade package to upscale buffet & full beverage
            service for $31/guest
          </i>
          <br />
          <br />• <b>Experience</b>: Two-tiered seating with extra-large bar,
          access to Fioptics District rooftop and personal attendant
          <br />
          <br />• <b>Seating</b>: Private throne-seating, bar stools, & patio
          furniture
          <br />
          <br />
          For more info{" "}
          <Link
            target="_blank"
            sx={{
              fontWeight: 500,
              color: "primary.main",
              textDecoration: "none",
            }}
            href="https://www.mlb.com/reds/tickets/group/skybar"
          >
            Click Here
          </Link>
          <br />
        </Typography>
      </ListItem>
    </List>
  </Box>
);

export const AllInclusiveSuiteContent = () => (
  <Box p={2} pt={1}>
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Typography
        variant="h6"
        sx={{
          fontWeight: 600,
          minWidth: "135px",
          fontSize: "1.25rem",
          color: "primary.main",
        }}
      >
        All-Inclusive Luxury Suite
      </Typography>
      <Divider sx={{ flex: 1, marginLeft: 2 }} orientation="horizontal" />
    </Stack>

    <List sx={{ py: 0 }}>
      <ListItem sx={{ paddingX: 0 }}>
        <Typography variant="body2">
          • <b>Tickets and Parking</b>: Each suite includes 16 tickets and 3
          parking passes for your group to enjoy
          <br />
          <br />• <b>Flash Sale Dates</b>: On select Flash Sale dates (Jul 4,
          Jul 27, Jul 28, Aug 4, Aug 5, Aug 17, Aug 18, Sep 1, Sep 2, Sep 5, and
          Sep 6), suites include 10 tickets with the option to add up to 6
          additional tickets at checkout
          <br />
          <br />• <b>Food and Beverage</b>: Includes an upscale, gourmet buffet,
          full beverage service, soft drinks and water
          <br />
          <br />• <b>Private Space</b>: Enjoy a private, climate-controlled
          environment complete with a dedicated restroom and your own personal
          Suite Attendant to keep your group cared for from first pitch to final
          out
          <br />
          <br />• <b>Seating</b>: Relax in interior and exterior seating
          featuring luxury furnishings and HD TVs, perfect for taking in the
          action in total comfort
          <br />
          <br />• <b>Additional Savings Available</b>: Looking for even more
          value? <strong>Our 5+ Game Suite</strong> Packages offer{" "}
          <strong>significant savings</strong>, exclusive perks, and
          customizable options when you purchase five (5) or more games
          <br />
          <br />• <b>Added Bonus</b>: A complimentary scoreboard message
          <br />
          <br />
          For more info{" "}
          <Link
            target="_blank"
            sx={{
              fontWeight: 500,
              color: "primary.main",
              textDecoration: "none",
            }}
            href="https://www.mlb.com/reds/tickets/premium/luxury-suites"
          >
            Click Here
          </Link>
          <br />
        </Typography>
      </ListItem>
    </List>
  </Box>
);

export const FujitecSuperSuiteContent = () => (
  <Box p={2} pt={1}>
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Typography
        variant="h6"
        sx={{
          fontWeight: 600,
          minWidth: "135px",
          fontSize: "1.25rem",
          color: "primary.main",
        }}
      >
        Fujitec Super Suite
      </Typography>
      <Divider sx={{ flex: 1, marginLeft: 2 }} orientation="horizontal" />
    </Stack>

    <List sx={{ py: 0 }}>
      <ListItem sx={{ paddingX: 0 }}>
        <Typography variant="body2">
          • <b>Capacity</b>: Accommodates 30-61 guests
          <br />
          <br />• <b>Food & Beverage</b>: All-inclusive standard buffet & two
          beers per guest -{" "}
          <i>
            opportunity to upgrade package to upscale buffet & full beverage
            service for $31/guest
          </i>
          <br />
          <br />• <b>Experience</b>: Private, spacious, climate-controlled suite
          down left-field line with private restroom and personal suite
          attendant
          <br />
          <br />• <b>Seating</b>: Combination of indoor lounge seating and
          outdoor ballpark seating with luxury furnishings and HD TVs
          <br />
          <br />• <b>Parking</b>: Includes 2 complimentary parking passes
          <br />
          <br />
          For more info{" "}
          <Link
            target="_blank"
            sx={{
              fontWeight: 500,
              color: "primary.main",
              textDecoration: "none",
            }}
            href="https://www.mlb.com/reds/tickets/group/left-field"
          >
            Click Here
          </Link>
          <br />
        </Typography>
      </ListItem>
    </List>
  </Box>
);

export const AnalyticsHeader = (props: { teamConfig: TeamConfig }) => {
  const { teamSchema } = props.teamConfig;

  return (
    <Head>
      <script
        nonce={process.env.pixelScriptNonce}
        id={`${teamSchema}-meta-pixel-script`}
        defer
        dangerouslySetInnerHTML={{
          __html: `
              !function(f,b,e,v,n,t,s)
              {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
              n.callMethod.apply(n,arguments):n.queue.push(arguments)};
              if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
              n.queue=[];t=b.createElement(e);t.async=!0;
              t.src=v;s=b.getElementsByTagName(e)[0];
              s.parentNode.insertBefore(t,s)}(window, document,'script',
              'https://connect.facebook.net/en_US/fbevents.js');
              fbq('init', '368362963346231');
              fbq('track', 'PageView');
              console.log('meta pixel page load event fired.');
              window.addEventListener("${constants.customEvents.eventPurchaseSuccess}", function(e) {
                try {
                  const { cartRes, checkoutRes } = e.detail;
                  fbq('track', 'Purchase', {
                    content_name: "${teamSchema}_event_purchase", 
                    value: String(cartRes.allInPrice), 
                    currency: 'USD', 
                    order_id: checkoutRes.orderNumber,
                  });
                  console.log('meta pixel purchase event fired.');
                } catch (error) {
                  console.log('Error in meta pixel purchase event', error); 
                }
              });
            `,
        }}
      />

      <noscript>
        <img
          height="1"
          width="1"
          style={{ display: "none" }}
          src="https://www.facebook.com/tr?id=368362963346231&ev=PageView&noscript=1"
        />
      </noscript>

      {/** tiktok script */}
      <script
        nonce={process.env.tiktokScriptNonce}
        defer
        id={`${teamSchema}-tik-tok-script`}
        dangerouslySetInnerHTML={{
          __html: `
              !(function (w, d, t) {
                w.TiktokAnalyticsObject = t;
                var ttq = (w[t] = w[t] || []);
                (ttq.methods = [
                  "page",
                  "track",
                  "identify",
                  "instances",
                  "debug",
                  "on",
                  "off",
                  "once",
                  "ready",
                  "alias",
                  "group",
                  "enableCookie",
                  "disableCookie",
                ]),
                  (ttq.setAndDefer = function (t, e) {
                    t[e] = function () {
                      t.push([e].concat(Array.prototype.slice.call(arguments, 0)));
                    };
                  });
                for (var i = 0; i < ttq.methods.length; i++)
                  ttq.setAndDefer(ttq, ttq.methods[i]);
                (ttq.instance = function (t) {
                  for (var e = ttq._i[t] || [], n = 0; n < ttq.methods.length; n++)
                    ttq.setAndDefer(e, ttq.methods[n]);
                  return e;
                }),
                  (ttq.load = function (e, n) {
                    var i = "https://analytics.tiktok.com/i18n/pixel/events.js";
                    (ttq._i = ttq._i || {}),
                      (ttq._i[e] = []),
                      (ttq._i[e]._u = i),
                      (ttq._t = ttq._t || {}),
                      (ttq._t[e] = +new Date()),
                      (ttq._o = ttq._o || {}),
                      (ttq._o[e] = n || {});
                    n = document.createElement("script");
                    (n.type = "text/javascript"),
                      (n.async = !0),
                      (n.src = i + "?sdkid=" + e + "&lib=" + t);
                    e = document.getElementsByTagName("script")[0];
                    e.parentNode.insertBefore(n, e);
                  });

                ttq.load("CBP9R33C77UB6N07V05G");
                ttq.page();
                console.log("tiktok pixel page view fired.");
              })(window, document, "ttq");

              window.addEventListener(
                "${constants.customEvents.eventPurchaseSuccess}",
                async function (e) {
                  var cartRes = e && e.detail && e.detail.cartRes;
                  var checkoutRes = e && e.detail && e.detail.checkoutRes;

                  ttq.identify({
                    email: e.detail.user.email,
                    phone_number: "+1" + e.detail.user.phoneNumber,
                  });

                  ttq.track("CompletePayment", {
                    content_id: checkoutRes.orderNumber,
                    content_name: "${teamSchema}_event_purchase",
                    quantity: String(cartRes.totalSeats),
                    price: String(cartRes.allInPrice),
                    value: String(cartRes.allInPrice),
                    currency: "USD",
                  });
                  console.log("tik tok pixel purchase event fired.");
                }
              );
            `,
        }}
      />
    </Head>
  );
};
